import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { Bell, MessageCircle, X } from "lucide-react";
import { useEffect } from "react";

// TODO: Org notifications: new follower, new review,
// TODO: User notifications: new follower, event updates (cancellation, time change, venue change, etc.), product update, shop update
const notifications = [
  {
    id: 1,
    type: "event_cancelled",
    title: "Event Cancelled",
    message:
      "The 'Local Art Exhibition' scheduled for next week has been cancelled.",
    time: "2 hours ago",
  },
  {
    id: 2,
    type: "new_items",
    title: "New Items Available",
    message: "The Cozy Corner Café has added new items to their menu.",
    time: "1 day ago",
  },
  {
    id: 3,
    type: "conversation",
    title: "New Message",
    message: "You have a new message from Community Farmers Market organizer.",
    time: "2 days ago",
    sender: {
      name: "John Doe",
      avatar: "https://i.pravatar.cc/150?img=1",
    },
  },
];

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationsModal({
  isOpen,
  onClose,
}: NotificationsModalProps) {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-cream">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h1 className="text-2xl font-semibold">Notifications</h1>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-6 w-6" />
          </Button>
        </div>
        <ScrollArea className="grow px-6 py-4">
          {notifications.length > 0 ? (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="flex items-start space-x-4 rounded-lg border p-4 shadow-xs"
                >
                  <div className="shrink-0">
                    {notification.type === "event_cancelled" && (
                      <div className="rounded-full bg-red-100 p-2">
                        <X className="h-5 w-5 text-red-600" />
                      </div>
                    )}
                    {notification.type === "new_items" && (
                      <div className="rounded-full bg-green-100 p-2">
                        <Bell className="h-5 w-5 text-green-600" />
                      </div>
                    )}
                    {notification.type === "conversation" && (
                      <div className="rounded-full bg-blue-100 p-2">
                        <MessageCircle className="h-5 w-5 text-blue-600" />
                      </div>
                    )}
                  </div>
                  <div className="grow">
                    <h3 className="text-sm font-medium">
                      {notification.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {notification.message}
                    </p>
                    {notification.type === "conversation" && (
                      <div className="mt-2 flex items-center space-x-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage
                            src={notification.sender?.avatar || ""}
                            alt={notification.sender?.name || ""}
                          />
                          <AvatarFallback>
                            {notification.sender?.name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs text-gray-500">
                          {notification.sender?.name}
                        </span>
                      </div>
                    )}
                    <p className="mt-1 text-xs text-gray-400">
                      {notification.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center space-y-4 text-center h-full">
              <Bell className="h-16 w-16 text-muted-foreground" />
              <h2 className="text-2xl font-semibold">No New Notifications</h2>
              <p className="text-muted-foreground">
                When you have new notifications, they&apos;ll appear here.
              </p>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
